import Spinner from '../ui/spinner';
import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Globe,
  Lock,
  Users,
  MessageCircle,
  TrendingUp,
  Shield,
  ArrowRightLeft,
  UserCog,
  Plus,
  Search,
  SquarePen,
} from 'lucide-react';

import { useQueryClient } from '@tanstack/react-query';

import { useTranslation } from '../../i18n';
import {
  useSpaceDetail,
  useUpdateSpace,
  spaceKeys,
} from '../../hooks/queries/useSpaceQueries';
import useModal from '../../hooks/useModal';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import { formatDate } from '../../utils/formatDate';
import { getImageUrl } from '../../utils/imageUrl';
import { IRoom, ISpace } from '../../types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import SpaceSettingsSection from '../SpaceSettingsSection';
import SpaceRoomSection from './SpaceRoomSection';
import SpaceFormDialog from './SpaceFormDialog';
import TransferOwnershipDialog from './TransferOwnershipDialog';
import ManageAdminsDialog from './ManageAdminsDialog';
import CreateRoomDialog from './CreateRoomDialog';
import ImagePreviewOverlay from '../ImagePreviewOverlay';

const SpaceDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [roomSearch, setRoomSearch] = useState('');
  const { data: spaceDetail, isLoading, refetch } = useSpaceDetail(id || '');
  const updateSpace = useUpdateSpace();

  // Dialog state
  const transferDialog = useModal();
  const adminsDialog = useModal();
  const createRoomDialog = useModal();
  const editDialog = useModal();
  const confirmDialog = useConfirmDialog();
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const allRooms: (IRoom & { messageCount?: number; memberCount?: number })[] =
    spaceDetail?.rooms || [];

  const filteredRooms = useMemo(() => {
    if (!roomSearch.trim()) return allRooms;

    const query = roomSearch.toLowerCase();

    return allRooms.filter((room) => {
      const nameMatch = room.name?.toLowerCase().includes(query);
      const adminMatch = room.roomMembers?.some(
        (m) =>
          m.role === 'admin' &&
          (m.user?.displayName?.toLowerCase().includes(query) ||
            m.userId?.toLowerCase().includes(query)),
      );

      return nameMatch || adminMatch;
    });
  }, [allRooms, roomSearch]);

  if (isLoading || !spaceDetail) {
    return <Spinner />;
  }

  const handleBack = () => navigate('/spaces');
  const handleNavigateRoom = (roomId: string) =>
    navigate(`/room-detail/${roomId}`);
  const handleNavigateUser = (userId: string) => navigate(`/users/${userId}`);

  const handleEditSpace = () => {
    editDialog.open();
  };

  const handleTransferOwnership = () => {
    transferDialog.open();
  };

  const handleManageAdmins = () => {
    adminsDialog.open();
  };

  const handleAddRoom = () => {
    createRoomDialog.open();
  };

  const handleToggleVisibility = async () => {
    const newVisibility =
      spaceDetail.visibility === 'private' ? 'public' : 'private';

    const confirmMsg =
      newVisibility === 'public'
        ? t('space_settings.confirm_make_public')
        : t('space_settings.confirm_make_private');

    confirmDialog.show(confirmMsg, async () => {
      try {
        await updateSpace.mutateAsync({
          id: spaceDetail.id,
          data: { visibility: newVisibility },
        });
        refetch();
      } catch (err: any) {
        alert(err.message || 'Failed to update visibility');
      }
    });
  };

  const handleSpaceUpdated = (updated: ISpace) => {
    // Optimistically update the query cache for immediate UI feedback
    if (id) {
      queryClient.setQueryData(spaceKeys.detail(id), (old: any) =>
        old ? { ...old, avatar: updated.avatar } : old,
      );
    }

    // Invalidate the space list so the outer layer reflects the new avatar
    queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });

    // Also refetch to ensure data is fully in sync with the server
    refetch();
  };

  const stats = [
    {
      label: t('spaces.space_members'),
      value: (spaceDetail.memberCount ?? 0).toLocaleString(),
      icon: <Users className="w-8 h-8 text-green-600" />,
    },
    {
      label: t('spaces.rooms'),
      value: (spaceDetail.roomCount ?? 0).toString(),
      icon: <MessageCircle className="w-8 h-8 text-purple-600" />,
    },
    {
      label: t('spaces.total_messages'),
      value: (spaceDetail.messageCount ?? 0).toLocaleString(),
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
    },
    {
      label: t('spaces.active_rooms'),
      value: (spaceDetail.roomCount ?? 0).toString(),
      icon: <Shield className="w-8 h-8 text-blue-600" />,
    },
  ];

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-8">
      {/* Back button */}
      <button
        onClick={handleBack}
        className="flex items-center gap-1 text-sm text-[#0A0A0A] hover:text-gray-900 w-fit"
      >
        <ArrowLeft className="w-4 h-4" /> {t('spaces.back_to_list')}
      </button>

      {/* Space header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            disabled={!spaceDetail.avatar}
            aria-label={t('common.view_avatar')}
            className={`w-16 h-16 rounded-lg bg-blue-500 flex items-center justify-center text-white overflow-hidden shrink-0${spaceDetail.avatar ? ' cursor-pointer' : ''}`}
            onClick={() => {
              if (spaceDetail.avatar)
                setPreviewImage(getImageUrl(spaceDetail.avatar)!);
            }}
          >
            {spaceDetail.avatar ? (
              <img
                src={getImageUrl(spaceDetail.avatar)!}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <Globe className="w-10 h-10" />
            )}
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">
                {spaceDetail.name}
              </h1>
              <Badge
                variant={
                  spaceDetail.visibility === 'private' ? 'purple' : 'info'
                }
                className="inline-flex items-center"
              >
                {spaceDetail.visibility === 'private' ? (
                  <Lock className="w-3 h-3 mr-1" />
                ) : (
                  <Globe className="w-3 h-3 mr-1" />
                )}
                {spaceDetail.visibility === 'private'
                  ? t('spaces.type_private')
                  : t('spaces.type_public')}
              </Badge>
            </div>
            <p className="text-gray-600 mt-2">
              {spaceDetail.description || '—'}
            </p>
            <div className="flex items-center gap-6 mt-3 text-sm text-gray-600">
              <span>
                {t('spaces.owner_label')}{' '}
                {spaceDetail.owner?.displayName ||
                spaceDetail.owner?.username ? (
                  <button
                    onClick={() => {
                      const ownerId =
                        spaceDetail.owner?.id || spaceDetail.ownerId;

                      if (ownerId) handleNavigateUser(ownerId);
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {spaceDetail.owner?.displayName ||
                      spaceDetail.owner?.username}
                  </button>
                ) : (
                  '—'
                )}
              </span>
              <span>·</span>
              <span>
                {t('spaces.created_at', {
                  date: formatDate(spaceDetail.createdAt),
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleEditSpace}
            className="inline-flex items-center gap-1.5"
          >
            <SquarePen className="w-4 h-4" /> {t('spaces.edit_info')}
          </Button>
          <Button
            variant="outline"
            onClick={handleTransferOwnership}
            className="inline-flex items-center gap-1.5"
          >
            <ArrowRightLeft className="w-4 h-4" />{' '}
            {t('spaces.transfer_ownership')}
          </Button>
          <Button
            variant="outline"
            onClick={handleManageAdmins}
            className="inline-flex items-center gap-1.5"
          >
            <UserCog className="w-4 h-4" /> {t('spaces.manage_admins')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/spaces/${spaceDetail.id}/members`)}
            className="inline-flex items-center gap-1.5"
          >
            <Users className="w-4 h-4" /> {t('spaces.view_space_members')}
          </Button>
          <Button
            variant="default"
            onClick={handleAddRoom}
            className="inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> {t('spaces.add_room')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon }) => (
          <Card key={label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {value}
                  </p>
                </div>
                {icon}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Space Settings */}
      <SpaceSettingsSection
        space={spaceDetail}
        onSpaceUpdated={handleSpaceUpdated}
        onToggleVisibility={handleToggleVisibility}
      />

      {/* Search */}
      <Card className="p-4 gap-0">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Search className="w-5 h-5" />
            </span>
            <Input
              type="text"
              value={roomSearch}
              onChange={(e) => setRoomSearch(e.target.value)}
              placeholder={t('spaces.search_rooms')}
              className="pl-10"
            />
          </div>
        </div>
      </Card>

      {/* Rooms Table */}
      <SpaceRoomSection
        rooms={filteredRooms}
        spaceOwner={spaceDetail.owner}
        onNavigateRoom={handleNavigateRoom}
        onViewMessages={(roomId) => navigate(`/room-messages/${roomId}`)}
        onViewMembers={(roomId) => navigate(`/room-members/${roomId}`)}
      />

      {/* Dialogs */}
      <SpaceFormDialog
        isOpen={editDialog.isOpen}
        onClose={() => {
          editDialog.close();
          refetch();
        }}
        space={spaceDetail}
      />
      <TransferOwnershipDialog
        isOpen={transferDialog.isOpen}
        onClose={transferDialog.close}
        space={spaceDetail}
      />
      <ManageAdminsDialog
        isOpen={adminsDialog.isOpen}
        onClose={adminsDialog.close}
        space={spaceDetail}
      />
      <CreateRoomDialog
        isOpen={createRoomDialog.isOpen}
        onClose={createRoomDialog.close}
        space={spaceDetail}
      />

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) => !open && confirmDialog.cancel()}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{t('common.confirm')}</DialogTitle>
            <DialogDescription>{confirmDialog.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={confirmDialog.cancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDialog.confirm}
              disabled={confirmDialog.isLoading}
            >
              {confirmDialog.isLoading
                ? t('common.saving')
                : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Overlay */}
      {previewImage && (
        <ImagePreviewOverlay
          src={previewImage}
          alt={spaceDetail.name}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

export default SpaceDetailView;
